import { Clear, Close, Summarize } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box, Card,
  CardContent,
  Chip,
  Container,
  Grid,
  IconButton,
  List,
  ListItem,
  Modal,
  Paper,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { filter, find, findIndex, groupBy, isEmpty, isEqual, map, orderBy, result } from "lodash";
import MaterialReactTable from "material-react-table";
import { FC, UIEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { withRouter } from "react-router-dom";
import { axiosInstance } from "../../../../services/axios";
import OperationModal from "../../../assets/OperationsModal";
import { DataCard } from "../../../dashboard";
import { ResultData } from "./ResultData";

import useFetch from "../../../../hooks/http/useFetch";
import useFetchOperations from "../../../../hooks/process/useFetchOperations";
import { useBreadcrumbState } from "../../../../store/breadcrumbState";
import { IChatState, useChatState } from "../../../../store/chatState";
import ModalWrapper from "../../../common/modal/wrapper";
import { IssueData } from "../issueComponents/IssueView";
import PerformanceTable from "./performanceTable";
import ResultNewView from "./ResultNewView";
import TabGenerator from "../../../settings/TabGenerator";
import CategoryBar from "../../../dashboard/widgetLibrary/categoryBar";
import SeverityDonut from "../../../dashboard/widgetLibrary/severityDonut";
import CategoryDistribution from "../../../dashboard/widgetLibrary/categoryDistribution";
import { maxHeight } from "@mui/system";
var format = require("string-template");

const OperationColumn: FC<any> = ({ operations, operationId }) => {
  const [operationData, setOperationData] = useState(null as any);

  useEffect(() => {
    try {
      setOperationData({
        ...find(operations, { operationID: operationId }),
        operationID: operationId,
      });
    } catch (error) {
      setOperationData({ operationID: operationId });
    }
  }, [operations, operationId]);

  if (!operationData) {
    return <></>;
  }
  return (
    <div>
      <Typography
        variant="caption"
        sx={{ fontSize: "12px" }}
        color="text.secondary"
      >
        {operationData?.["resource"]}
      </Typography>
      <Box>
        <Typography variant="overline" color="success.main">
          {operationData?.["method"]} &nbsp;
        </Typography>
        <Typography sx={{ display: "inline" }} variant="h6">
          {operationData?.["path"]}
        </Typography>
      </Box>
      <Typography variant="caption" sx={{ fontSize: "12px" }}>
        {operationData?.["operationID"]}
      </Typography>
    </div>
  );
};

const Summary: FC<any> = ({result, operationsTableData}) => {
  return (
    <>
                        {result?.toolInfo?.type === 'DEPENDENCY' && <DependencySummary result={result} />}
                        {result?.toolInfo?.type === "API" && <APISummary
                          columns={operationsTableData?.columns}
                          rows={operationsTableData?.data}
                        />}
                      </>
  )
}

const ResultView: FC<any> = ({ history, resultId, location }) => {
  const [selectedFindingId, setSelectedFindingId] = useState(
    null as string | null
  );
  const [selectedTab, setSelectedTab] = useState<"Faults" | "Performance">(
    "Faults"
  );
  const [result, setResult] = useState({} as any);
  const [isResultInaccessible, setIsResultInaccessible] = useState(false);
  const [issueId, setIssueId] = useState("");
  const [selectedFaultGroup, setSelectedFaultGroup] = useState("");
  const [operationsTableData, setOperationsTableData] = useState<any>({});
  const [openOperationModal, setOpenOperationModal] = useState(false);
  const [selectedOperationId, setSelectedOperationId] = useState(null);
  const [assetId, setApiId] = useState(null);
  const { data: api, loading: apiLoading } = useFetch(result?.assetId ? `/apis/${result?.assetId}` : undefined);
  const [revId, setRevId] = useState(null);
  const { operations } = useFetchOperations("result", result?.assetId, resultId);
  const [activeTab, setActiveTab] = useState("issues");
  const [riskIssueData, setRiskIssueData] = useState({});
  const [fetchedRiskIssueData, setFetchedRiskIssueData] = useState<any>(null);

  const fetchRiskIssueDataUrl = useMemo(() => {
    let baseUrl = "/insights/issues?type=risk";
    if (result?.projectId) {
      baseUrl =  `${baseUrl}&projectId=${result?.projectId}`;
    }
    if (assetId) {
      baseUrl =  `${baseUrl}&assetId=${assetId}`;
    }
    if (result?.environmentId) {
      baseUrl = `${baseUrl}&environmentId=${result?.environmentId}`;
    }
    if (result?.id) {
      baseUrl =  `${baseUrl}&resultId=${result?.id}`;
    }
    return baseUrl;
  }, [assetId, result]);

  const fetchRiskIssueData = async() => {
    try {
      const response = await axiosInstance.get(fetchRiskIssueDataUrl);
      setFetchedRiskIssueData(response?.data)
    } catch (error) {
      
    }
  }

  useEffect(() => {
    if(result && result?.projectId && result?.environmentId) {
      fetchRiskIssueData()
    }
  }, [result])

  useEffect(() => {
    if (fetchedRiskIssueData) {
      const newData: any = {};
      fetchedRiskIssueData.byRisk.forEach((fault: any) => {
        if (fault?.risk != null) {
          newData[fault.risk] = fault.total ?? 0;
        }
      });
    
      setRiskIssueData(newData);
    }
  }, [fetchedRiskIssueData]);
  
  const { setScope } = useChatState((state) => state) as IChatState
  const setOverrideBreadcrumb = useBreadcrumbState(
    //@ts-ignore
    state => state.setOverrideBreadcrumb)

  useEffect(() => {
    fetchResult();
  }, []);

  const fetchResult = async () => {
    try {
      setScope({ "result_uuid": resultId })
      let response = await axiosInstance.get(format(`/results/${resultId}`));
      setResult(response?.data);
      setIsResultInaccessible(false);
    } catch (error: any) {
      setIsResultInaccessible(true);
    }
  };

  useEffect(() => {
    let newBreadcrumb = {
      group: {
        id: result?.groupId,
        name: result?.groupName
      }, project: {
        id: result?.projectId,
        name: result?.projectName
      }
    }
    setOverrideBreadcrumb(newBreadcrumb)
  }
    , [result, setOverrideBreadcrumb])

  useEffect(() => {
    if (!isEmpty(result) && !isEmpty(operations)) {
      let newFaultAnalyzerColumns: any[] = [
        {
          header: "Operations",
          accessorKey: "operationID", // accessor key for the cell

          Cell: (params: any) => (
            <OperationColumn
              operations={operations}
              operationId={params?.row?.original?.["operationID"]}
            />
          ),
        },
      ];
      if (!isEmpty(result?.faultAnalyzers)) {
        for (let analyzer of result?.faultAnalyzers) {
          try {
            newFaultAnalyzerColumns.push({
              header: analyzer?.displayName,
              accessoryKey: analyzer?.name,
              type: "custom",
              Cell: (params: any) => {
                const analyzerStatus = params?.row?.original?.[analyzer?.name]
                try {
                  if (!analyzerStatus) {
                    return <></>
                  }
                  return (
                    analyzerStatus.toLowerCase() === "fail" ? (<Chip label='Issue' color="error" variant="outlined" size='small'/>) : 
                    (<Typography variant={ 'body2'} color={'text.primary'}>{analyzerStatus.toLowerCase() === "pass" ? "No Issue" : analyzerStatus.toLowerCase() === "fail" ? "Issue" : analyzerStatus}</Typography>)

                    
                  );
                } catch (error) { }
                return null;
              },
            });
          } catch (error) { }
        }
      }
      let newFaultAnalyzerData = [];
      let newFaultAnalyzerStatusData = [];
      if (
        !isEmpty(result?.metrics) &&
        !isEmpty(result?.metrics?.perOperation)
      ) {
        for (let operation of result?.metrics?.perOperation) {
          let object: any = { operationID: operation?.operationId };
          newFaultAnalyzerStatusData.push({
            ...object,
            failedRequests: operation.requestsFailed,
            successfulRequests: operation.requestsSuccessful,
            status: operation.requestsSuccessful > 0 ? "PASS" : "FAIL",
          });
          for (let analyzer of operation?.perAnalyzer) {
            object[analyzer?.analyzerName] = analyzer?.decision;
          }
          newFaultAnalyzerData.push(object);
        }
      }
      setOperationsTableData({
        data: orderBy(
          newFaultAnalyzerData.map((o) => ({
            ...o,
            operationId: o.operationID,
            resource: find(operations, { operationID: o.operationID })
              ?.resource,
          })),
          "resource",
          "asc"
        ),
        columns: newFaultAnalyzerColumns,
      });
    }
  }, [result, operations]);

  const handleCloseOperationModal = () => {
    setSelectedOperationId(null);
    setOpenOperationModal(false);
  };

  const tabs = [
    {
      label: "Issues",
      value: "issues",
      component: <ResultNewView result={result} operations={operations} api={api} view={'table'}/>,
    },
  ];

  const tabsForApi =[ {
      label: "Resources",
      value: "resource",
      component:<ResultNewView result={result} operations={operations} api={api} view={'resource'}/>,
    },
    {
      label: "Performance",
      value: "performance",
      component: <Performance result={result}/>,
    },
  ];

  const tabsForAPIAndDependency = [
    {
      label: "Summary",
      value: "summary",
      component: <Summary result={result} operationsTableData={operationsTableData}/>,
    },
  ]

  if(result?.toolInfo?.type === "API") {
    tabs.push(...tabsForApi)
  }
  if(result?.toolInfo?.type === "API" || result?.toolInfo?.type === "DEPENDENCY") {
    tabs.push(...tabsForAPIAndDependency)
  }


  if (isResultInaccessible) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          <Typography variant="h4">
            Run is inaccessible
          </Typography>
          <Typography variant="body1">
            The run you are trying to access is either deleted or you do not have access to view it in this organization
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <Grid container columnSpacing={3}>
        <Grid xs={12} md={resultId ? 12 : 8}>
          <Card
            elevation={0}
            sx={{
              pl: 2,
              textAlign: "left",
            }}
          >
            <Modal
              open={openOperationModal}
              onClose={handleCloseOperationModal}
            >
              <OperationModal
                onClose={handleCloseOperationModal}
                selectedOperation={{
                  assetId: assetId!,
                  revId: revId!,
                  defaultOperationId: selectedOperationId,
                }}
              />
            </Modal>

            <ResultData
              id={resultId}
              api={{ ...api, assetLabels: result?.assetLabels, projectLabels: result?.projectLabels }}
              result={result}
              history={history}
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
              onFaultsGroupChange={(value: string) =>
                setSelectedFaultGroup(value)
              }
              riskIssueData={riskIssueData}
            />
            {/* {resultId && <AiSuggestions prompt={`Severity can be one of "critical, high, medium, low, information". List two top severity issues`} scope={
              {
                result_uuid: resultId
              }
            } />} */}
            {/* <AIChat scope={{
              result_uuid: resultId
            }} /> */}
            
            <TabGenerator tabs={tabs} activeTab={activeTab} halfWidth={true} sx={{overflow: "hidden"}}/>
            

          </Card>
        </Grid>
      </Grid>
      <Modal
        open={selectedFindingId != null}
        onClose={() => setSelectedFindingId(null)}
      >
        <Box
          sx={{
            minHeight: "100%",
            overflow: "auto",
            p: 3,
          }}
        >
          <Container maxWidth="lg">
            <Paper elevation={12} sx={{ p: 3 }}>
              <Close
                className="hand"
                onClick={() => setSelectedFindingId(null)}
              />

              <Box sx={{ maxHeight: "80vh", overflow: "auto" }}>
                <IssueData
                  id={issueId}
                  history={history}
                  findingId={selectedFindingId}
                />
              </Box>
            </Paper>
          </Container>
        </Box>
      </Modal>
    </>
  );
};

const Performance: FC<any> = ({result}) => {
  return(
    <PerformanceTable result={result} />
  )
};

const Charts: FC<any> = ({result}) => {
  return(
    <Box sx={{ display: "flex", justifyContent: "space-evenly", ml: 2 }}>
              <Grid container spacing={2} sx={{ width: "100%", mt: 1, mr: 2, border: "2px solid #5148e626", backgroundColor: "#5048e508", borderRadius: "0.35em", p: 3 }}>
                <Grid item xs={12} md={6}>
                  <CategoryDistribution groupId={result?.groupId} runId={result?.id} height="200px" maxDisplayCount={4} disableClick  sx={{width: "100%"}}/>
                </Grid>
                <Grid item xs={12} md={6}>
                  <SeverityDonut runId={result?.id} height="200px" disableClick />
                </Grid>
              </Grid> 
    
            </Box>
  )
};


const DependencySummary = ({ result }: any) => {
  const { data } = useFetch(`insights/issues/summary?resultId=${result?.id}`)


  if (data?.packages?.length === 0) {
    return <Typography variant="body2">No actions required</Typography>
  }

  return <>
    <List sx={{maxHeight: '50vh', overflow: 'auto'}}>
      {data?.packages?.map((pkg: any) => (

        <ListItem>
          <Chip label={pkg.action} size="small" color="info" />
          <Typography variant="body2" sx={{ ml: 1 }}>
            {pkg.package}   <Chip label={"Current: " + pkg.currentVersion} size="small" color="default" />
          </Typography>
        </ListItem>
      ))}
    </List>

  </>

}


const APISummary: React.FC<any> = ({ rows = [], columns = [] }) => {
  console.log(rows, columns)
  const [rowsToShow, setRowsToShow] = useState(rows.slice(0, 50));
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        //once the user has scrolled within 400px of the bottom of the table, fetch more data if we can
        if (
          scrollHeight - scrollTop - clientHeight < 400 &&

          rowsToShow.length < rows.length
        ) {
          setRowsToShow((prev: any) => rows.slice(0, Math.min(prev.length + 50, rows.length)));
        }
      }
    },
    [setRowsToShow, rowsToShow]
  );


  useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current);
  }, [fetchMoreOnBottomReached, tableContainerRef]);




  return (
    <Box sx={{p:2}}>


          <MaterialReactTable muiTablePaperProps={{
            elevation: 0
          }} enableGlobalFilter={false}
            columns={columns}
            data={rowsToShow}


            initialState={{
              density: "compact",
              columnPinning: { left: ['operationID'] },
            }}
            enableColumnActions={false}
            enableSorting={false}

            enableHiding={false}
            enableFullScreenToggle={false}
            enableStickyHeader

            enableTopToolbar={false}
            enableBottomToolbar={false}
            enablePagination={false}
            enableDensityToggle={false}
            enableColumnFilters={false}
            // enableColumnPinning={true}



            muiTableContainerProps={{
              ref: tableContainerRef, //get access to the table container element
              sx: { maxHeight: "50vh" }, //give the table a max height
              onScroll: (
                event: UIEvent<HTMLDivElement> //add an event listener to the table container element
              ) => {


                fetchMoreOnBottomReached(event.target as HTMLDivElement)

              }

            }}
          />

    </Box>
  );
};

export default withRouter(ResultView);
